import React, { useMemo } from 'react';
import '../TodaysSalesReport.css';
import '../banquet/banquet.css';

/**
 * Missing Ingredients — the TSR page's popup, shared by every lane that asks
 * for negative-stock consent (banquet, Not in Menu).
 *
 * Same title, same styling (TSR's mi-modal-*-unique classes), the same
 * "Confirm & Create Negative Stock". TSR's second button, "Remove and Update
 * Recipe", is deliberately absent: it rewrites the dish's recipe for the
 * restaurant too, which is the wrong side effect of recording one event.
 *
 * It takes BOTH row shapes: the pre-check's (`itemName`) and the engine's
 * (`menuItemName`, when the server finds a shortage the pre-check did not).
 */
export const normaliseShortages = (rows) => (rows || []).map((r) => ({
  dishName: r.itemName || r.menuItemName || 'Item',
  ingredientName: r.ingredientName || 'Ingredient',
  required: r.required,
  available: r.available,
  unit: r.unit || r.availableUnit || '',
  isComplimentary: Boolean(r.isComplimentary),
}));

/** Small amounts keep three decimals, so 4 g of a kg-stocked item never reads as "0". */
const qty = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v ?? '');
  if (Number.isInteger(n)) return String(n);
  return Math.abs(n) < 1 ? String(Math.round(n * 1000) / 1000) : String(Math.round(n * 100) / 100);
};

const ShortageConsentModal = ({
  shortages,
  busy,
  onConfirm,
  onCancel,
  description = null,
  confirmLabel = 'Confirm & Create Negative Stock',
}) => {
  const groups = useMemo(() => {
    const byDish = new Map();
    for (const r of shortages || []) {
      if (!byDish.has(r.dishName)) byDish.set(r.dishName, []);
      byDish.get(r.dishName).push(r);
    }
    return [...byDish.entries()];
  }, [shortages]);

  return (
    <div className="mi-modal-overlay-unique" onClick={busy ? undefined : onCancel}>
      <div
        className="mi-modal-content-unique"
        role="dialog"
        aria-label="Missing ingredients"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mi-modal-header-unique">
          <h3 className="mi-modal-title-unique">Missing Ingredients</h3>
          <button type="button" className="mi-modal-close-unique" onClick={onCancel} disabled={busy} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="mi-modal-body-unique">
          {description && <p className="mi-modal-description-unique">{description}</p>}
          {groups.map(([dish, rows]) => (
            <div key={dish} className="bq-shortage-group">
              <h4 className="bq-shortage-dish">{dish}</h4>
              {rows.map((r, i) => (
                <div key={`${dish}-${i}`} className="bq-shortage-row">
                  <span className="mi-ingredient-name-unique">
                    {r.ingredientName}
                    {r.isComplimentary && <span className="bq-comp-badge">COMPLIMENTARY</span>}
                  </span>
                  <span className="mi-ingredient-details-unique">
                    Required: {qty(r.required)} {r.unit} | Available: {qty(r.available)} {r.unit}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="mi-modal-footer-unique">
          <button
            type="button"
            className="mi-modal-btn-unique mi-btn-negative-unique"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Recording…' : confirmLabel}
          </button>
          <button
            type="button"
            className="mi-modal-btn-unique mi-btn-remove-unique"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShortageConsentModal;
